<template>
  <div>
    <transition name="fade" mode="out-in">
      <div class="screen-wrap">
        <v-app class="hmc-app">
          <v-main class="main-body">
            <v-container class="app-body">
              <div class="pe-2 ps-3 app-title text-h5">
                {{ $t("model.name.missions") }}
                <v-spacer></v-spacer>
                <CloseBtn size="40" :callback="exit" />
              </div>
              <Loader v-if="api.isLoading" :isLoading="api.isLoading" />
              <div v-else>
                <div v-if="!isLoading" style="max-width: 800px" class="mx-auto">
                  <!-- Ongoing Missions Section Only -->
                  <div v-if="ongoingMissions.length > 0" class="mb-6">
                    <v-card
                      v-for="(item, i) in ongoingMissions"
                      :key="'ongoing-' + i"
                      class="mb-3"
                      flat
                      outlined
                    >
                      <div class="pa-3">
                        <v-row align="center" no-gutters>
                          <v-col cols="2">
                            <v-avatar size="32" color="primary">
                              <span class="white--text font-weight-bold">{{ i + 1 }}</span>
                            </v-avatar>
                          </v-col>
                          <v-col cols="10" class="pl-3">
                            <div class="font-weight-medium text-body-1">
                              {{ item.questionName || item.questionKey }}
                            </div>
                          </v-col>
                        </v-row>
                        <v-row class="mt-3" align="center" no-gutters>
                          <v-col cols="4" class="text-center">
                            <div class="text-caption text--secondary mb-1">{{ $t("model.prop.grade") }}</div>
                            <v-img
                              contain
                              max-width="35"
                              class="mx-auto"
                              :src="gradeShields[item.mathRecord && item.mathRecord.grade ? item.mathRecord.grade : 'X']"
                            ></v-img>
                          </v-col>
                          <v-col cols="4" class="text-center">
                            <div class="text-caption text--secondary mb-1">{{ $t("model.prop.stars") }}</div>
                            <v-rating
                              :value="item.mathRecord.topStar"
                              background-color="amber"
                              color="amber"
                              length="3"
                              readonly
                              dense
                              size="18"
                            ></v-rating>
                          </v-col>
                          <v-col cols="4" class="text-center">
                            <v-btn
                              color="primary"
                              @click="playMission(item)"
                            >
                              <v-icon class="mr-1">mdi-play</v-icon>
                              {{ $t("action.play") }}
                            </v-btn>
                          </v-col>
                        </v-row>
                      </div>
                    </v-card>
                  </div>
                  <div v-if="ongoingMissions.length === 0 && !isLoading" class="text-center mt-8">
                    <v-card flat class="mx-auto" style="max-width: 400px;">
                      <v-card-text class="pa-8">
                        <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-clipboard-list</v-icon>
                        <div class="text-h6 mb-2 text--secondary">{{ $t("string.missionsNotFound") }}</div>
                      </v-card-text>
                    </v-card>
                  </div>
                </div>
              </div>
            </v-container>
          </v-main>
        </v-app>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  components: {
    CloseBtn: () => import("@/components/btns/CloseBtn"),
    Loader: () => import("@/components/Loader.vue"),
  },
  computed: {
    ...mapState({
      settings: (state) => state.settings.data,
      auth: (state) => state.auth.data,
    }),
    ongoingMissions() {
      return this.missions
        .filter(record => record.mathRecord && !record.mathRecord.isCompleted)
        .map(record => {
          const questionData = this.questionsData.find(q => q.key === record.questionKey);
          return {
            ...record,
            questionName: questionData ? questionData.name : record.questionKey
          };
        });
    },
  },
  data() {
    return {
      api: {
        isLoading: false,
        isError: false,
        error: null,
        url: null,
      },
      apiQuestion: {
        isLoading: false,
        isError: false,
        error: null,
        url: null,
      },
      missions: [],
      questionsData: [],
      isLoading: false,
      gradeShields: {
        A: require('@/assets/math/shields/gradeA.png'),
        B: require('@/assets/math/shields/gradeB.png'),
        C: require('@/assets/math/shields/gradeC.png'),
        F: require('@/assets/math/shields/gradeF.png'),
        X: require('@/assets/math/shields/gradeX.png')
      },
    };
  },
  async created() {
    await this.getMission();
  },
  methods: {
    exit() {
      this.$router.push({ name: "PageWorldNews" });
    },
    async getMission() {
      this.missions = [];
      this.isLoading = true;
      this.api.url =
        this.$api.servers.game +
        "/api/v1/" +
        this.$_getLocale() +
        "/game/mission/visit";
      this.api.callbackReset = () => {
        this.api.isLoading = true;
        this.api.isError = false;
        this.api.error = null;
      };
      this.api.callbackError = (e) => {
        this.api.isLoading = false;
        this.api.isError = true;
        this.api.error = e;
        this.isLoading = false;
      };
      this.api.callbackSuccess = async (resp) => {
        this.api.isLoading = false;
        this.missions = resp.Mission || [];
        await this.fetchQuestionNames();
        this.isLoading = false;
      };
      await this.$api.fetch(this.api);
    },
    playMission(mission) {
      this.$router.push({
        name: "PageMathSelectGame",
        params: { topic: mission.questionKey },
        query: { topic: mission.questionName },
      });
    },
    async fetchQuestionNames() {
      if (this.missions.length === 0) return;

      // Get unique question keys
      const questionKeys = [...new Set(this.missions.map(mission => mission.questionKey))];
      
      // Fetch question data for each key
      for (const key of questionKeys) {
        await this.fetchSingleQuestion(key);
      }
    },
    async fetchSingleQuestion(questionKey) {
      this.apiQuestion.method = "post";
      this.apiQuestion.url = `${process.env.VUE_APP_URL_API_QUESTION}/v2/questions`;

      this.apiQuestion.callbackReset = () => {
        this.apiQuestion.isLoading = true;
        this.apiQuestion.isError = false;
        this.apiQuestion.error = null;
      };
      
      this.apiQuestion.callbackError = (e) => {
        this.apiQuestion.isLoading = false;
        this.apiQuestion.isError = true;
        this.apiQuestion.error = e;
        console.error('Error fetching question:', questionKey, e);
      };
      
      this.apiQuestion.callbackSuccess = (resp) => {
        this.apiQuestion.isLoading = false;
        if (resp && resp.length > 0) {
          this.questionsData.push(resp[0]);
        }
      };
      
      this.apiQuestion.params = {
        key: questionKey,
        lang: this.$_getLocale(),
        count: 1,
        showOption: true
      };
      
      await this.$api.fetch(this.apiQuestion);
    },
  },
};
</script>

<style scoped>
.app-title {
  z-index: 99;
  position: fixed;
  top: 0;
  max-width: 480px !important;
  width: 100%;
  display: flex;
  align-items: center;
  height: 60px;
  box-shadow: 0px 5px 2px rgba(128, 128, 128, 0.5) !important;
  border: #caad87 5px solid;
  margin: auto;
  color: #fff;
  background-color: #f7efe3;
  text-shadow: #cbb18f 3px 0px 0px, #cbb18f 2.83487px 0.981584px 0px,
    #cbb18f 2.35766px 1.85511px 0px, #cbb18f 1.62091px 2.52441px 0px,
    #cbb18f 0.705713px 2.91581px 0px, #cbb18f -0.287171px 2.98622px 0px,
    #cbb18f -1.24844px 2.72789px 0px, #cbb18f -2.07227px 2.16926px 0px,
    #cbb18f -2.66798px 1.37182px 0px, #cbb18f -2.96998px 0.42336px 0px,
    #cbb18f -2.94502px -0.571704px 0px, #cbb18f -2.59586px -1.50383px 0px,
    #cbb18f -1.96093px -2.27041px 0px, #cbb18f -1.11013px -2.78704px 0px,
    #cbb18f -0.137119px -2.99686px 0px, #cbb18f 0.850987px -2.87677px 0px,
    #cbb18f 1.74541px -2.43999px 0px, #cbb18f 2.44769px -1.73459px 0px,
    #cbb18f 2.88051px -0.838247px 0px;
}
.main-body {
  background-color: rgba(214, 132, 49, 0.3);
}
.app-body {
  max-width: 480px;
  min-height: 100%;
  padding: 80px 0 40px 0;
}
</style>
